import React, { useState, useEffect, useCallback } from 'react';
import { Square, Undo2, ShieldAlert, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}`, 'Content-Type': 'application/json' });
const BASE = `${API_URL}/api/admin/gastos/gmail/limpiar-no-facturas`;

const DeletedList = ({ items }) => (
  <details className="mt-2 text-xs" data-testid="cleanup-deleted-list">
    <summary className="cursor-pointer font-medium">Ver lo eliminado ({items.length})</summary>
    <ul className="mt-1 max-h-48 overflow-y-auto space-y-0.5">
      {items.map(d => (
        <li key={d.id} className="text-gray-600">{d.date || '—'} · {d.vendor || '—'} · {d.amount != null ? `${d.amount} €` : '—'} · {(d.document_type || 'sin NIF/base').replace('_', ' ')}</li>
      ))}
    </ul>
  </details>
);

const RestoreList = ({ items, onChange }) => {
  const force = async (id) => {
    const res = await fetch(`${API_URL}/api/admin/gastos/gmail/recuperar/${id}`, { method: 'POST', headers: headers() });
    if (!res.ok) return toast.error('No se pudo recuperar');
    toast.success('Recuperada');
    onChange();
  };
  return (
    <details className="mt-2 text-xs" open data-testid="restore-list">
      <summary className="cursor-pointer font-medium">Ver documentos revisados ({items.length})</summary>
      <ul className="mt-1 max-h-64 overflow-y-auto space-y-1">
        {items.map(d => (
          <li key={d.image_id} className="flex items-center gap-2 text-gray-700">
            <span className={`px-1.5 py-0.5 rounded font-bold ${d.restored ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{d.restored ? `Recuperada (${d.where})` : 'Sigue eliminada'}</span>
            <span className="truncate flex-1">{d.date || '—'} · {d.vendor || '—'} · {d.amount != null ? `${d.amount} €` : '—'} · {(d.document_type || '').replace('_', ' ')}</span>
            <a href={`${API_URL}/api/images/file/${d.image_id}`} target="_blank" rel="noreferrer" className="underline">ver</a>
            {!d.restored && <button onClick={() => force(d.image_id)} className="underline font-medium text-blue-700" data-testid={`restore-force-${d.image_id}`}>Recuperar igualmente</button>}
          </li>
        ))}
      </ul>
    </details>
  );
};

const CleanupStatus = ({ job, reload }) => {
  const restore = job?.mode === 'restore';
  if (!job || job.status === 'none') return null;
  const active = job.status === 'running';
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
  const tone = ['error', 'cancelled'].includes(job.status) ? 'bg-red-50 text-red-700' : active ? 'bg-blue-50 text-blue-800' : 'bg-green-50 text-green-800';
  return (
    <div className={`rounded-lg p-3 text-sm ${tone}`} data-testid="cleanup-status">
      <p className="font-medium flex items-center gap-1.5">
        {active ? <RefreshCw size={14} className="animate-spin" /> : ['error', 'cancelled'].includes(job.status) ? <AlertTriangle size={14} /> : <CheckCircle size={14} />}
        {active ? (restore ? 'Recuperando facturas eliminadas…' : 'Revisando documentos…') : ['error', 'cancelled'].includes(job.status) ? job.error : (restore ? 'Recuperación terminada' : 'Revisión terminada')}
      </p>
      <div className="h-1.5 bg-white rounded-full mt-2"><div className="h-1.5 rounded-full bg-current transition-all" style={{ width: `${pct}%` }} /></div>
      <p className="text-xs mt-1.5" data-testid="cleanup-counts">
        {restore ? `${job.done}/${job.total} revisados · ${job.deleted} facturas recuperadas · ${job.kept} siguen eliminadas` : `${job.done}/${job.total} revisados · ${job.deleted} eliminados · ${job.kept} facturas correctas`}{job.errors ? ` · ${job.errors} sin poder leer` : ''}
      </p>
      {job.deleted_items?.length > 0 && <DeletedList items={job.deleted_items} />}
      {job.restore_items?.length > 0 && <RestoreList items={job.restore_items} onChange={reload} />}
    </div>
  );
};

export const GmailCleanup = ({ onFinished }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const load = useCallback(async () => {
    const res = await fetch(`${BASE}/latest`, { headers: headers() });
    if (res.ok) setJob(await res.json());
  }, []);
  useEffect(() => { load(); }, [load]);
  const active = job?.status === 'running';
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [active, load]);
  useEffect(() => { if (job?.status === 'done') onFinished?.(); }, [job?.status, onFinished]);

  const stop = async () => {
    if (!window.confirm('¿Detener ahora? Lo ya procesado se mantiene.')) return;
    const res = await fetch(`${BASE}/cancel`, { method: 'POST', headers: headers() });
    if (!res.ok) return toast.error('No se pudo detener');
    toast.success('Detenida');
    load();
  };

  const restore = async () => {
    if (!window.confirm('Se volverán a leer con IA todos los documentos que borró la limpieza y se recuperarán los que sean facturas. ¿Continuar?')) return;
    const res = await fetch(`${API_URL}/api/admin/gastos/gmail/recuperar-eliminados`, { method: 'POST', headers: headers() });
    const j = await res.json();
    if (!res.ok) return toast.error(j.detail || 'No se pudo iniciar');
    setJob(j);
    toast.success('Recuperación iniciada');
  };

  const start = async () => {
    if (!window.confirm('La IA revisará todo lo importado de Gmail y BORRARÁ lo que no sea una factura completa (con NIF y base imponible): avisos de pago, comisiones, albaranes, confirmaciones… No se puede deshacer. ¿Continuar?')) return;
    setStarting(true);
    const res = await fetch(BASE, { method: 'POST', headers: headers() });
    const j = await res.json();
    setStarting(false);
    if (!res.ok) return toast.error(j.detail || 'No se pudo iniciar');
    setJob(j);
    toast.success('Revisión iniciada');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden" data-testid="gmail-cleanup">
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
        <ShieldAlert size={18} className="text-red-600" />
        <h3 className="font-semibold text-gray-900">Limpiar lo que no son facturas</h3>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-xs text-gray-500">Revisa con IA cada documento importado de Gmail (gastos y no reconocidos) y elimina los que no sean una factura completa con NIF y base imponible.</p>
        <Button onClick={start} disabled={starting || active} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" data-testid="cleanup-start-btn">
          {starting ? <RefreshCw size={14} className="animate-spin mr-2" /> : <ShieldAlert size={14} className="mr-2" />}
          Revisar y eliminar no-facturas
        </Button>
        <Button onClick={restore} disabled={starting || active} variant="outline" className="ml-2 border-green-200 text-green-700 hover:bg-green-50" data-testid="restore-start-btn">
          <Undo2 size={14} className="mr-2" /> Recuperar facturas eliminadas
        </Button>
        {active && (
          <Button onClick={stop} variant="outline" className="ml-2 border-gray-300" data-testid="cleanup-stop-btn">
            <Square size={14} className="mr-2" /> Detener
          </Button>
        )}
        <CleanupStatus job={job} reload={load} />
      </div>
    </div>
  );
};
